import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaFont } from 'react-icons/fa';
import { storiesAPI, uploadAPI } from '../utils/api';
import { toast } from 'sonner';
import StoriesViewer from './StoriesViewer';

const StoriesBar = () => {
  const [stories, setStories] = useState([]);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showTextStoryModal, setShowTextStoryModal] = useState(false);
  const [textStoryContent, setTextStoryContent] = useState('');
  const [textStoryBg, setTextStoryBg] = useState('from-blue-500 to-purple-600');
  const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');

  const bgOptions = [
    'from-blue-500 to-purple-600',
    'from-pink-500 to-rose-500',
    'from-green-400 to-emerald-600',
    'from-orange-400 to-red-500',
    'from-indigo-500 to-blue-600',
    'from-yellow-400 to-orange-500',
  ];

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      const response = await storiesAPI.getActive();
      setStories(response.data || []);
    } catch (error) {
      console.error('Failed to load stories', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }

    setUploading(true);
    try {
      const uploadResponse = await uploadAPI.uploadMedia(file);
      await storiesAPI.create({
        media_url: uploadResponse.data.url,
        media_type: uploadResponse.data.type
      });
      toast.success('Story posted!');
      loadStories();
    } catch (error) {
      toast.error('Failed to post story');
    } finally {
      setUploading(false);
    }
  };

  const handleTextStoryCreate = async () => {
    if (!textStoryContent.trim()) {
      toast.error('Please enter some text');
      return;
    }
    setUploading(true);
    try {
      await storiesAPI.create({
        media_url: '',
        media_type: 'text',
        content: textStoryContent,
        background: textStoryBg
      });
      toast.success('Story posted!');
      setShowTextStoryModal(false);
      setTextStoryContent('');
      loadStories();
    } catch (error) {
      toast.error('Failed to post story');
    } finally {
      setUploading(false);
    }
  };

  const openStory = (index) => {
    setSelectedUserIndex(index);
    setShowViewer(true);
  };

  // Check if current user already has a story
  const currentUserHasStory = stories.some(s => s.user?.id === currentUser?.id);

  return (
    <>
      <div className="bg-white border-b border-gray-200 p-4" data-testid="stories-bar">
        <div className="flex space-x-4 overflow-x-auto" style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          {/* Add story button */}
          <div className="flex-shrink-0" data-testid="add-story-section">
            <div className="flex flex-col items-center space-y-1">
              <label className="cursor-pointer" data-testid="add-story-image-btn">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A90E2] to-[#A855F7] flex items-center justify-center hover:opacity-90 transition-opacity">
                    {uploading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <FaPlus className="text-white text-lg" />
                    )}
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                  data-testid="story-file-input"
                />
              </label>
              <p className="text-xs text-center text-gray-700">Your Story</p>
            </div>
            <button
              onClick={() => setShowTextStoryModal(true)}
              className="mt-1 text-[10px] text-blue-500 hover:text-blue-700 flex items-center gap-1 mx-auto"
              data-testid="add-text-story-btn"
            >
              <FaFont className="text-[8px]" /> Text
            </button>
          </div>

          {/* User stories */}
          {stories.map((userStory, index) => (
            <button
              key={userStory.user?.id || index}
              onClick={() => openStory(index)}
              className="flex-shrink-0"
              data-testid={`story-user-${userStory.user?.username || index}`}
            >
              <div className="relative">
                <div className="w-16 h-16 rounded-full p-[2px] bg-gradient-to-br from-[#4A90E2] via-[#4DD9E8] to-[#A855F7]">
                  <img
                    src={userStory.user?.avatar_url || 'https://via.placeholder.com/64'}
                    alt={userStory.user?.full_name || 'User'}
                    className="w-full h-full rounded-full object-cover border-2 border-white"
                  />
                </div>
                {userStory.stories?.length > 1 && (
                  <span className="absolute -bottom-0 -right-0 bg-blue-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">
                    {userStory.stories.length}
                  </span>
                )}
                <p className="text-xs text-center mt-1 text-gray-700 truncate w-16">
                  {userStory.user?.id === currentUser?.id ? 'You' : userStory.user?.username}
                </p>
              </div>
            </button>
          ))}

          {stories.length === 0 && !uploading && (
            <div className="flex items-center text-gray-400 text-sm pl-4" data-testid="no-stories-text">
              No stories yet. Be the first to share!
            </div>
          )}
        </div>
      </div>

      {/* Text Story Modal */}
      {showTextStoryModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="text-story-modal">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md mx-4"
          >
            <h3 className="text-lg font-bold mb-4">Create Text Story</h3>
            
            {/* Preview */}
            <div className={`w-full h-48 rounded-xl bg-gradient-to-br ${textStoryBg} flex items-center justify-center p-4 mb-4`}>
              <p className="text-white text-center font-semibold text-lg">
                {textStoryContent || 'Your story text...'}
              </p>
            </div>

            {/* Text input */}
            <textarea
              value={textStoryContent}
              onChange={(e) => setTextStoryContent(e.target.value)}
              placeholder="What's on your mind?"
              className="w-full border border-gray-300 rounded-lg p-3 mb-3 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              maxLength={200}
              data-testid="text-story-input"
            />
            <p className="text-xs text-gray-400 mb-3">{textStoryContent.length}/200</p>

            {/* Background colors */}
            <div className="flex space-x-2 mb-4">
              {bgOptions.map((bg, i) => (
                <button
                  key={`bg-${i}`}
                  onClick={() => setTextStoryBg(bg)}
                  className={`w-8 h-8 rounded-full bg-gradient-to-br ${bg} ${textStoryBg === bg ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                  data-testid={`bg-option-${i}`}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={() => { setShowTextStoryModal(false); setTextStoryContent(''); }}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                data-testid="cancel-text-story-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleTextStoryCreate}
                disabled={!textStoryContent.trim() || uploading}
                className="flex-1 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                data-testid="post-text-story-btn"
              >
                {uploading ? 'Posting...' : 'Post Story'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showViewer && stories.length > 0 && (
        <StoriesViewer
          stories={stories}
          initialUserIndex={selectedUserIndex}
          onClose={() => setShowViewer(false)}
          onStoryDeleted={loadStories}
        />
      )}
    </>
  );
};

export default StoriesBar;
